import { isAbortError } from '@app/errors/AbortError'

import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'

import { asError } from '@app/packages/asError/asError'
import { ReactiveValue } from '@app/packages/reactives/ReactiveValue'

export const ResourceEnded = Symbol('ResourceEnded')

export type ResourceLoader<Data, State> = (
  state: State | undefined,
  abortController: AbortController
) => Promise<readonly [Data, State | typeof ResourceEnded] | undefined>
export type ResourceMerger<Data> = (currentData: Data | undefined, newData: Data) => Data | undefined

export class Resource<Data, State> {
  private loader: ResourceLoader<Data, State>
  private merger: ResourceMerger<Data>

  readonly data: ReactiveValue<Data | undefined>
  readonly loading: ReactiveValue<boolean>
  readonly error: ReactiveValue<Error | null>
  readonly ended: ReactiveValue<boolean>
  readonly initialLoadTime: ReactiveValue<Moment | null>
  readonly lastLoadTime: ReactiveValue<Moment | null>
  private abortController?: AbortController
  private state?: State

  constructor(loader: ResourceLoader<Data, State>, merger: ResourceMerger<Data> = (_o, n) => n, data?: Data, state?: State) {
    this.loader = loader
    this.merger = merger

    this.ended = new ReactiveValue(false)
    this.data = new ReactiveValue<Data | undefined>(data)
    this.loading = new ReactiveValue(false)
    this.error = new ReactiveValue<Error | null>(null)
    this.initialLoadTime = new ReactiveValue<Moment | null>(null)
    this.lastLoadTime = new ReactiveValue<Moment | null>(null)
    this.state = state
  }

  async load(reset = false) {
    if (!reset && this.ended.value) return
    if (!reset && this.loading.value) return
    this.ended.update(() => false)
    this.loading.update(() => true)
    this.error.update(() => null)
    if (reset || !this.abortController) {
      this.initialLoadTime.update(() => null)
      this.abortController?.abort()
      this.abortController = new AbortController()
    }
    if (reset) this.state = undefined
    const abortController = this.abortController
    try {
      const result = await this.loader(this.state, abortController)
      if (!result) return
      const [newData, state] = result
      if (abortController.signal.aborted) return
      this.data.update(data => this.merger(reset ? undefined : data, newData))
      const time = moment()
      this.initialLoadTime.update(val => val ?? time)
      this.lastLoadTime.update(() => time)
      if (state === ResourceEnded) {
        this.ended.update(() => true)
      } else {
        this.state = state
      }
    } catch (e) {
      if (isAbortError(e)) return
      if (abortController.signal.aborted) return
      this.error.update(() => asError(e))
      throw e
    } finally {
      if (!abortController.signal.aborted) {
        this.loading.update(() => false)
      }
    }
  }

  async loadSilent(reset = false) {
    await this.load(reset).catch(() => {})
  }

  reset() {
    this.abortController?.abort()
    this.abortController = undefined
    this.data.update(() => undefined)
    this.error.update(() => null)
    this.loading.update(() => false)
    this.initialLoadTime.update(() => null)
    this.lastLoadTime.update(() => null)
    this.state = undefined
    this.ended.update(() => false)
  }

  resetError() {
    this.error.update(() => null)
  }

  abort() {
    this.abortController?.abort()
  }
}
